export default function ArticlePartTwo() {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-3xl font-bold text-gray-900 mb-6">When Does a Quick Cash Sale Make Sense?</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <img 
            src="https://public.readdy.ai/ai/img_res/d91458a4dc04b72607d264c177952f10.jpg"
            alt="Homeowner facing urgent sale situation"
            className="w-full h-96 object-cover object-top rounded-lg mb-6"
          />
        </div>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Not every seller needs the speed of a cash sale. If you have time to wait for top dollar and your property is in excellent condition, a traditional listing might net you more money. However, certain situations make cash sales not just convenient, but necessary.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Facing Foreclosure</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          When you're behind on mortgage payments and the foreclosure clock is ticking, time is your enemy. A traditional sale takes too long, and once the foreclosure auction date is set, you may have only weeks to act. A cash sale can close before the auction, allowing you to pay off your mortgage, avoid foreclosure on your credit report, and potentially walk away with some equity instead of losing everything.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Job Relocation</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Military families stationed at Fort Bliss and professionals relocating for work often receive short notice to move. Carrying two mortgages or paying rent in a new city while waiting for your El Paso house to sell creates significant financial strain. A quick cash sale allows you to close before you leave, eliminating the stress of managing a long-distance sale.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Inherited Property</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Inheriting a house often comes with unexpected costs: property taxes, insurance, utilities, and maintenance. If you live out of state or can't afford to maintain the property, these expenses pile up quickly. Many inherited homes also need significant repairs before they're market-ready. Selling for cash as-is eliminates these burdens and converts the property into liquid assets you can divide among heirs or use for other purposes.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Divorce Settlement</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Divorce proceedings are emotionally and financially draining. When the court orders the marital home to be sold, both parties want the process finished quickly so they can move forward with their lives. A cash sale provides a clean break, allowing you to split the proceeds and close this chapter without months of continued joint property ownership.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Property Needs Major Repairs</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          If your house needs a new roof, foundation repairs, electrical updates, or other costly fixes, traditional buyers will either walk away or demand significant price reductions. Getting repair estimates, hiring contractors, and overseeing work takes months and requires upfront capital you might not have. Cash buyers purchase properties in any condition, allowing you to sell immediately without spending a dime on repairs.
        </p>

        <div className="bg-teal-50 rounded-lg p-6 mb-6">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Real El Paso Example</h4>
          <p className="text-gray-700 mb-4">
            Maria inherited her grandmother's house in the Lower Valley. The property needed $35,000 in repairs including roof replacement, plumbing updates, and cosmetic work. She lived in Dallas and couldn't manage the renovations or afford the upfront costs. Property taxes and insurance were costing her $400 monthly.
          </p>
          <p className="text-gray-700">
            She contacted a cash buyer who offered $145,000 for the house as-is. After paying off the remaining $82,000 mortgage and closing costs, Maria netted $58,000 and closed in 10 days. Had she tried to sell traditionally, she would have spent months and thousands of dollars on repairs with no guarantee of a higher net profit.
          </p>
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Cash Offers: What to Expect</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <img 
            src="https://readdy.ai/api/search-image?query=real%20estate%20cash%20offer%20document%20with%20calculator%20and%20house%20valuation%20report%20showing%20fair%20market%20analysis%20professional%20desk%20setup%20clean%20organized%20business%20environment&width=1200&height=600&seq=cashsale004&orientation=landscape"
            alt="Cash offer evaluation"
            className="w-full h-96 object-cover object-top rounded-lg mb-6"
          />
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">How Cash Buyers Calculate Offers</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Cash buyers, particularly investors, use a formula to determine their maximum offer price. Understanding this formula helps you evaluate whether an offer is fair and competitive.
        </p>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h4 className="text-xl font-bold text-gray-900 mb-4">The 70% Rule (For Fix-and-Flip Investors)</h4>
          <p className="text-gray-700 mb-4">
            <strong>Maximum Offer = (After Repair Value × 70%) - Repair Costs - Profit Margin</strong>
          </p>
          <p className="text-gray-700 mb-4">Example for an El Paso property:</p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-teal-600 mr-3">•</span>
              <span>After Repair Value (ARV): $220,000</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3">•</span>
              <span>Estimated Repairs: $30,000</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3">•</span>
              <span>Desired Profit: $25,000</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3">•</span>
              <span>Maximum Offer: ($220,000 × 0.70) - $30,000 - $25,000 = $99,000</span>
            </li>
          </ul>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">Buy-and-Hold Investors</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Investors who plan to rent the property use different calculations based on rental income potential and long-term appreciation. They typically offer higher prices than fix-and-flip investors because they're not looking for quick profits. In El Paso, with strong rental demand from Fort Bliss and UTEP, buy-and-hold investors are active buyers.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">iBuyers and Instant Offer Companies</h3>
        <p className="text-lg leading-relaxed text-gray-700 mb-6">
          Companies like Opendoor and Offerpad use algorithms to generate instant offers. These offers are typically 5% to 10% below market value, and they charge service fees ranging from 5% to 7%. While convenient, these companies often provide lower net proceeds than working with local cash buyers who have lower overhead costs.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 mb-4">What Affects Your Cash Offer Amount</h3>
        <div className="space-y-4 mb-6">
          <div className="border-l-4 border-teal-600 pl-4">
            <h4 className="font-bold text-gray-900 mb-2">Property Condition</h4>
            <p className="text-gray-700">The more repairs needed, the lower the offer. Major issues like foundation problems, roof damage, or outdated electrical systems significantly impact value.</p>
          </div>
          <div className="border-l-4 border-teal-600 pl-4">
            <h4 className="font-bold text-gray-900 mb-2">Location</h4>
            <p className="text-gray-700">Properties in desirable El Paso neighborhoods like West Side, East Side near Fort Bliss, or areas with good schools command higher offers.</p>
          </div>
          <div className="border-l-4 border-teal-600 pl-4">
            <h4 className="font-bold text-gray-900 mb-2">Market Conditions</h4>
            <p className="text-gray-700">In a hot seller's market, cash buyers compete more aggressively. In a buyer's market, offers tend to be more conservative.</p>
          </div>
          <div className="border-l-4 border-teal-600 pl-4">
            <h4 className="font-bold text-gray-900 mb-2">Comparable Sales</h4>
            <p className="text-gray-700">Recent sales of similar properties in your neighborhood establish the baseline for offers.</p>
          </div>
          <div className="border-l-4 border-teal-600 pl-4">
            <h4 className="font-bold text-gray-900 mb-2">Your Timeline</h4>
            <p className="text-gray-700">If you need to close in 7 days versus 30 days, some buyers may adjust their offer based on the urgency and risk involved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}